/**
 * Read-only view of the house rules a game was actually started with,
 * opened by tapping the Fun/Serious Mode badge during play. Settings are
 * snapshotted at game start (CLAUDE.md rule 5) and never change mid-game,
 * so this is deliberately just a preview — no save button, no inputs that
 * actually do anything.
 */

import type { HkosSettings } from '../rules/hkos';
import { renderHouseRulesSummary } from './views/settings';

let overlay: HTMLDivElement | undefined;

function close(): void {
  overlay?.remove();
  overlay = undefined;
  document.removeEventListener('keydown', onKeydown);
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') close();
}

/** What the mode badge says: a leaderboard game is a Leaderboard game first, whatever style its rules use. */
export function modeLabel(game: { leaderboard?: unknown }, settings: HkosSettings): string {
  if (game.leaderboard) return 'Leaderboard';
  return settings.mode === 'fun' ? 'Fun Mode' : 'Serious Mode';
}

/**
 * `title` is only passed for a leaderboard game (or the pre-game rules preview), whose rules belong to the
 * venue and to the whole leaderboard period — not to this game alone.
 */
export function showHouseRulesPreview(settings: HkosSettings, title?: string): void {
  close();

  const el = document.createElement('div');
  el.className = 'lightbox-overlay';
  el.innerHTML = `
    <div class="card modal-panel">
      <h1>${title ?? (settings.mode === 'fun' ? 'Fun Mode' : 'Serious Mode')}</h1>
      <p class="muted">
        ${
          title
            ? "Set by the venue for the whole leaderboard period — nobody can change them. This is just a preview."
            : "Locked in for this game — settings can't be changed mid-game. This is just a preview."
        }
      </p>
      ${renderHouseRulesSummary(settings)}
      <button type="button" id="closeHouseRulesPreview" class="secondary" style="width:100%;margin-top:1rem">
        Close
      </button>
    </div>
  `;

  // Tapping the backdrop closes it; tapping inside the panel shouldn't.
  el.addEventListener('click', close);
  el.querySelector('.modal-panel')!.addEventListener('click', (e) => e.stopPropagation());
  el.querySelector('#closeHouseRulesPreview')!.addEventListener('click', close);
  document.addEventListener('keydown', onKeydown);

  document.body.appendChild(el);
  overlay = el;
}
